<template lang="pug">
  .controls(:class="direction === 'vertical' && 'vertical'" tabindex="-1")
    .control-container
      RogueButton.ctrl(
        ref="prevBtn"
        :class="prevHidden && 'hidden'"
        :aria-label="__(prevLabel)"
        :aria-hidden="prevHidden || null"
        :tabindex="prevHidden ? -1 : null"
        @click="$emit('update:modelValue', modelValue - 1)"
      )
        Icon(:name="direction === 'vertical' ? 'arrow-up' : 'arrow-left'" fill="#D2D2D2")
      .spacer
      RogueButton.ctrl(
        ref="nextBtn"
        :class="nextHidden && 'hidden'"
        :aria-label="__(nextLabel)"
        :aria-hidden="nextHidden || null"
        :tabindex="nextHidden ? -1 : null"
        @click="$emit('update:modelValue', modelValue + 1)"
      )
        Icon(:name="direction === 'vertical' ? 'arrow-down' : 'arrow-right'" fill="#D2D2D2")
</template>
<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch, type ComponentPublicInstance } from 'vue'

const props = withDefaults(defineProps<{
  modelValue?: number,
  direction?: string,
  max?: number,
  prevLabel?: string,
  nextLabel?: string,
}>(), {
  modelValue : 0,
  direction  : 'horizontal',
  max        : 0,
  prevLabel  : 'Previous items',
  nextLabel  : 'Next items',
})
defineEmits(['update:modelValue'])

const prevHidden = computed(() => props.modelValue < 1)
const nextHidden = computed(() => props.modelValue >= props.max - 1)

const prevBtn = useTemplateRef<ComponentPublicInstance>('prevBtn')
const nextBtn = useTemplateRef<ComponentPublicInstance>('nextBtn')

// An arrow hides at the end of the range; if it had keyboard focus, hand focus to the other arrow
// instead of leaving it on a control that is invisible and hidden from screen readers
function moveFocusOffHidden(hiding: ComponentPublicInstance | null, other: ComponentPublicInstance | null) {
  const el = hiding?.$el as HTMLElement | undefined
  if (el && typeof document !== 'undefined' && el.contains(document.activeElement)) {
    nextTick(() => (other?.$el as HTMLElement | undefined)?.focus())
  }
}

watch(prevHidden, hidden => hidden && moveFocusOffHidden(prevBtn.value, nextBtn.value))
watch(nextHidden, hidden => hidden && moveFocusOffHidden(nextBtn.value, prevBtn.value))
</script>
<style lang="scss" scoped>

.controls {
  user-select: none;
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  justify-content: space-between;
  align-items: center;
  overflow: visible;
}

.ctrl {
  pointer-events: all;
  background-color: $color-gray-x-light;
  padding: $space-xxs;
  opacity: $opacity-disabled;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: opacity 0.1s ease;
  z-index: 9;
  border: none;

  &.hidden {
    pointer-events: none;
    opacity: 0 !important; // needed
  }

  &:hover {
    opacity: 1;
  }

  .icon {
    height: 18px;
    width: 18px;
  }
}

.control-container {
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  left: $space-s;
  right: $space-s;
  bottom: 0;

  &.outside {
    left: -$space-s;
    right: -$space-s;

    .ctrl {
      opacity: 1;
    }
  }
}

.controls {
  margin: 0 auto;
}

.controls.vertical .control-container {
  flex-direction: column;
  right: 0;
  left: 0;

  .ctrl {
    opacity: 0.8;
    border: 1px solid $color-gray;
    padding: $space-xxxs;

    &:hover {
      opacity: 1.0;
      border-color: $color-gray-dark;
    }
  }

  .icon {
    width: 100%;
    height: $space-xs;
  }
}

.spacer {
  flex-grow: 1;
}

</style>
